import type { FieldTree } from '@angular/forms/signals';
import { validateStandardSchema } from '@angular/forms/signals';

import type { LegacyVideoDefault, LegacyVideoZero, VideoEntity } from '@sites/entity-core';
import { videoLegacyDefaultV0Schema, videoLegacyZeroV0Schema } from '@sites/entity-core';

import type { BaseLegacyEntityForm, BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { configureCommonReadonly, createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { composeForm } from '../../core/compose-form';
import { animationPlugin } from '../../plugins/widget/animation.plugin';
import { extendedPositionPlugin } from '../../plugins/widget/position.plugin';
import { visibilityPlugin } from '../../plugins/widget/visibility.plugin';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type VideoFormModel = VideoEntity;
export type VideoDataModel = VideoEntity['data'];

/**
 * Video-specific accessors shared verbatim by both variant assemblers — the
 * source/preview/misc fields. These are structurally identical on both
 * variants (the variants diverge only on layout, contributed per assembler),
 * so the lazy `raw` thunk is narrowed to the default variant's FieldTree
 * internally.
 *
 * Exposed via getters so the FieldTree paths materialise lazily (after
 * `syncFromEntity` populates the model) — composed with {@link composeForm},
 * never object spread.
 */
function videoSharedAccessors(raw: () => FieldTree<LegacyVideoDefault> | FieldTree<LegacyVideoZero>) {
    const r = raw as () => FieldTree<LegacyVideoDefault>;
    return {
        // Source
        get videoType() {
            return r().data.videoType;
        },
        setVideoType: (value: VideoDataModel['videoType']) => {
            r().data.videoType().value.set(value);
        },
        get videoUrl() {
            return r().data.videoUrl;
        },
        setVideoUrl: (value: VideoDataModel['videoUrl']) => {
            r().data.videoUrl().value.set(value);
        },
        get videoFile() {
            return r().data.videoFile;
        },
        setVideoFile: (value: VideoDataModel['videoFile']) => {
            r().data.videoFile().value.set(value);
        },

        // Preview
        get previewUrl() {
            return r().data.previewUrl;
        },
        setPreviewUrl: (value: VideoDataModel['previewUrl']) => {
            r().data.previewUrl().value.set(value);
        },
        get customPreviewFile() {
            return r().data.customPreviewFile;
        },
        setCustomPreviewFile: (value: VideoDataModel['customPreviewFile']) => {
            r().data.customPreviewFile().value.set(value);
        },
        get customPreviewUrl() {
            return r().data.customPreviewUrl;
        },
        setCustomPreviewUrl: (value: VideoDataModel['customPreviewUrl']) => {
            r().data.customPreviewUrl().value.set(value);
        },
        get customPreviewSize() {
            return r().data.customPreviewSize;
        },
        setCustomPreviewSize: (value: VideoDataModel['customPreviewSize']) => {
            r().data.customPreviewSize().value.set(value);
        },

        // Misc
        get placeholder() {
            return r().data.placeholder;
        },
        setPlaceholder: (value: VideoDataModel['placeholder']) => {
            r().data.placeholder().value.set(value);
        },
        get isShorts() {
            return r().data.isShorts;
        },
        setIsShorts: (value: VideoDataModel['isShorts']) => {
            r().data.isShorts().value.set(value);
        },

        // Play button — size preset + idle/hover colours. Shared by both
        // variants (the field lives on the common data base). Each colour is a
        // separate leaf so the color-picker bindings update independently.
        get playButton() {
            return r().data.playButton;
        },
        get playButtonSize() {
            return r().data.playButton.size;
        },
        setPlayButtonSize: (value: VideoDataModel['playButton']['size']) => {
            r().data.playButton.size().value.set(value);
        },
        get playButtonBackgroundColor() {
            return r().data.playButton.backgroundColor;
        },
        setPlayButtonBackgroundColor: (value: VideoDataModel['playButton']['backgroundColor']) => {
            r().data.playButton.backgroundColor().value.set(value);
        },
        get playButtonIconColor() {
            return r().data.playButton.iconColor;
        },
        setPlayButtonIconColor: (value: VideoDataModel['playButton']['iconColor']) => {
            r().data.playButton.iconColor().value.set(value);
        },
        get playButtonHoverBackgroundColor() {
            return r().data.playButton.hoverBackgroundColor;
        },
        setPlayButtonHoverBackgroundColor: (value: VideoDataModel['playButton']['hoverBackgroundColor']) => {
            r().data.playButton.hoverBackgroundColor().value.set(value);
        },
        get playButtonHoverIconColor() {
            return r().data.playButton.hoverIconColor;
        },
        setPlayButtonHoverIconColor: (value: VideoDataModel['playButton']['hoverIconColor']) => {
            r().data.playButton.hoverIconColor().value.set(value);
        },
    };
}

/**
 * Per-variant accessor assemblers. The default and zero variants share every
 * **video-source** accessor (via {@link videoSharedAccessors}) but diverge on
 * **layout**: the default form exposes `horizontalPosition`/`maxWidth`, the
 * zero form exposes `position` (extended), `hideOn*`, `animation`,
 * `isSynchronizeStyle` — contributed by the shared widget plugins.
 *
 * The first `composeForm` part widens the inherited base methods
 * (`syncFromEntity`, `setValue`, `value`) to operate on `VideoEntity` so the
 * dispatcher's union return collapses into a single useful shape for callers
 * that only know the union (e.g. `WebLegacyWidgetSignalFormService`).
 */
function assembleDefaultVideoForm(base: BaseLegacyEntityForm<LegacyVideoDefault>) {
    // `_raw` stays typed as `FieldTree<LegacyVideoDefault>` so each accessor
    // resolves to the correct nested `FieldTree<…>` (e.g. `_raw.data.videoUrl`
    // is `FieldTree<string>`, `_raw.data.maxWidth` is `FieldTree<number | null>`).
    const _raw = base._raw;

    // Checked widening (no cast): the variant form is assignable to the
    // entity-union form, so the dispatcher's union return collapses into a
    // single useful shape for union-only callers.
    const widenedBase: BaseLegacyEntityForm<VideoEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        videoSharedAccessors(() => _raw),

        // Default-only layout — the horizontalPosition/maxWidth shorthand.
        {
            get horizontalPosition() {
                return _raw.data.horizontalPosition;
            },
            setHorizontalPosition: (value: LegacyVideoDefault['data']['horizontalPosition']) => {
                _raw.data.horizontalPosition().value.set(value);
            },
            get maxWidth() {
                return _raw.data.maxWidth;
            },
            setMaxWidth: (value: LegacyVideoDefault['data']['maxWidth']) => {
                _raw.data.maxWidth().value.set(value);
            },
        }
    );
}

function assembleZeroVideoForm(base: BaseLegacyEntityForm<LegacyVideoZero>) {
    // See {@link assembleDefaultVideoForm} — `_raw` is the narrow
    // `FieldTree<LegacyVideoZero>`, which gives every nested accessor its
    // correct `FieldTree<…>` type (e.g. `_raw.data.position` is
    // `FieldTree<ExtendedPosition>`, `_raw.data.hideOnTablet` is
    // `FieldTree<boolean>`).
    const _raw = base._raw;

    const widenedBase: BaseLegacyEntityForm<VideoEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        videoSharedAccessors(() => _raw),

        // Zero-only layout — the shared zero-widget setting groups.
        visibilityPlugin(() => _raw.data),
        animationPlugin(() => _raw.data),
        extendedPositionPlugin(() => _raw.data),

        // Zero-only sync flag — schema pins this to `false` literal but the
        // editor may temporarily flip it during settings interactions; consumers
        // observe via the FieldTree.
        {
            get isSynchronizeStyle() {
                return _raw.data.isSynchronizeStyle;
            },
        }
    );
}

/**
 * Signal-form view of a **default-placement** video widget. Validates against
 * the legacy default envelope schema, which permits an absent `type` field for
 * parity with pre-discriminator persisted data on disk.
 *
 * **Field accessors are {@link import('@angular/forms/signals').FieldTree} references** —
 * exposed via getters so the FieldTree paths materialise lazily (after
 * `syncFromEntity` populates the model). Consumers call them to get the
 * `FieldState`, then read `.value()`, `.touched()`, `.dirty()`, `.errors()`
 * as needed. Convenience `setX(v)` methods are still provided as sugar over
 * `field().value.set(v)`.
 *
 * ```ts
 * form.videoUrl().value()    // current url
 * form.videoUrl().touched()  // touched flag (auto-tracked only with signal-forms bindings)
 * form.videoUrl().errors()   // ValidationError[] from the schema's superRefine
 * form.setVideoUrl('https://…')   // sugar over form.videoUrl().value.set(…)
 * ```
 */
export function createLegacyVideoDefaultForm(options?: BaseLegacyEntityFormOptions<LegacyVideoDefault>) {
    const base = createBaseLegacyEntityForm<LegacyVideoDefault>((schemaPath) => {
        validateStandardSchema(schemaPath, videoLegacyDefaultV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    // The assembler takes the narrow `BaseLegacyEntityForm<LegacyVideoDefault>`
    // so its internal `_raw` keeps the precise FieldTree shape (and the
    // accessor getters resolve to typed `FieldTree<…>` returns). The
    // public-facing widening to `VideoEntity` happens inside the assembler,
    // via a checked assignment on the base part.
    return assembleDefaultVideoForm(base);
}

/**
 * Signal-form view of a **zero-block-placement** video widget. Validates
 * against the legacy zero envelope schema, which requires `type: 'zero'` on
 * the envelope (the host-app factory copies `meta.type` onto the entity at
 * creation time).
 *
 * Diverges from {@link createLegacyVideoDefaultForm} on layout: exposes
 * `position` (extended-position grid), `hideOnDesktop`/`hideOnTablet`/
 * `hideOnMobile`, `animation`, and `isSynchronizeStyle` instead of the
 * default variant's `horizontalPosition` + `maxWidth` shorthand.
 */
export function createLegacyVideoZeroForm(options?: BaseLegacyEntityFormOptions<LegacyVideoZero>) {
    const base = createBaseLegacyEntityForm<LegacyVideoZero>((schemaPath) => {
        validateStandardSchema(schemaPath, videoLegacyZeroV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    // See `createLegacyVideoDefaultForm` — narrow base in, widening happens
    // inside the assembler.
    return assembleZeroVideoForm(base);
}

/**
 * Variant dispatcher. Callers that only know `VideoEntity` (the union of
 * default + zero) use this — `WebLegacyWidgetSignalFormService` for instance — and the
 * dispatcher reads the variant tag from `options.initialData?.type` to pick
 * the matching per-variant factory.
 *
 * The return type is the **union** of the two variant forms. Consumers that
 * want to read variant-specific accessors (e.g. `form.position` on zero,
 * `form.maxWidth` on default) must narrow via `form.value().type === 'zero'`
 * first, or use the variant factories directly to skip narrowing.
 */
export function createVideoForm(options?: BaseLegacyEntityFormOptions<VideoEntity>) {
    const variant = options?.initialData?.type;

    if (variant === 'zero') {
        return createLegacyVideoZeroForm(options as BaseLegacyEntityFormOptions<LegacyVideoZero>);
    }

    return createLegacyVideoDefaultForm(options as BaseLegacyEntityFormOptions<LegacyVideoDefault>);
}

export type VideoForm = ReturnType<typeof createVideoForm>;
export type VideoDefaultForm = ReturnType<typeof createLegacyVideoDefaultForm>;
export type VideoZeroForm = ReturnType<typeof createLegacyVideoZeroForm>;
